import { Compass, MessageCircle, MapPin, ShieldCheck } from "lucide-react";
import Seo from "../components/Seo";
import EnquiryForm from "../components/EnquiryForm";
import { Reveal } from "../components/Motion";
import { useLang } from "../lib/lang";
import { WHATSAPP_NUMBER, WHATSAPP_DISPLAY } from "../lib/content";

export default function Contact() {
  const { t } = useLang();

  return (
    <div>
      <Seo title={t.meta.contact.title} description={t.meta.contact.desc} />

      <section className="mx-auto max-w-6xl px-5 pb-24 pt-12 sm:pt-16" data-testid="contact-page">
        <Reveal y={12}>
          <p className="eyebrow">{t.contact.eyebrow}</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl leading-tight text-ink sm:text-5xl lg:text-6xl">
            {t.contact.title}
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#475569] sm:text-lg">{t.contact.sub}</p>
        </Reveal>

        <div className="mt-12 grid gap-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <Reveal>
              <h2 className="font-display text-2xl text-ink">{t.contact.formTitle}</h2>
              <div className="mt-5">
                <EnquiryForm />
              </div>
            </Reveal>
          </div>

          <aside className="space-y-6 lg:col-span-2">
            <Reveal delay={0.1}>
              <div className="card-warm p-7" data-testid="contact-how-card">
                <h3 className="flex items-center gap-2.5 font-display text-xl text-ink">
                  <Compass size={20} className="text-saffron" />
                  {t.contact.aside.howTitle}
                </h3>
                <ol className="mt-5 space-y-4">
                  {t.contact.aside.howItems.map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-[#475569]">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink text-[11px] font-bold text-cream">
                        {i + 1}
                      </span>
                      {it}
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="rounded-3xl bg-ink p-7 text-cream" data-testid="contact-direct-card">
                <h3 className="font-display text-xl">{t.contact.aside.directTitle}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-cream/75">{t.contact.aside.directText}</p>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-direct-whatsapp-link"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-saffron px-6 py-3 text-sm font-semibold text-[#FDFBF7] transition-all duration-300 hover:-translate-y-0.5 hover:bg-cream hover:text-ink"
                >
                  <MessageCircle size={17} />
                  {WHATSAPP_DISPLAY}
                </a>
                <p className="mt-5 flex items-center gap-2 text-xs text-cream/60">
                  <MapPin size={14} className="text-marigold" />
                  {t.contact.aside.location}
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.26}>
              <div className="rounded-3xl border-2 border-dashed border-[#E5DECF] bg-[#FDFBF7] p-7" data-testid="contact-privacy-card">
                <h3 className="flex items-center gap-2.5 font-display text-xl text-ink">
                  <ShieldCheck size={20} className="text-saffron" />
                  {t.contact.privacy.title}
                </h3>
                <ul className="mt-5 space-y-3.5">
                  {t.contact.privacy.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-[#475569]">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-saffron" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
    </div>
  );
}
